import { useRef, useState } from "react";
import { Calendar, Clock, CheckCircle2, DollarSign, Info, FileVideo, UploadCloud, X } from "lucide-react";
import { Reveal, SectionTag } from "@/components/Reveal";
import { api } from "@/context/AuthContext";
import { toast } from "sonner";

// Theo's live Google Calendar embed (public calendar for trainingcoachtheo@gmail.com).
const GOOGLE_CALENDAR_EMBED_URL = "https://calendar.google.com/calendar/embed?src=trainingcoachtheo%40gmail.com&ctz=America%2FLos_Angeles";

const WEEKLY_SLOTS = [
  { day: "Monday", slots: [{ time: "3:30 PM", type: "Group Drop-in" }, { time: "5:00 PM", type: "Private 1-on-1" }, { time: "6:30 PM", type: "Private 1-on-1" }] },
  { day: "Tuesday", slots: [{ time: "4:00 PM", type: "Group Drop-in" }, { time: "5:30 PM", type: "Private 1-on-1" }] },
  { day: "Wednesday", slots: [{ time: "3:30 PM", type: "Group Drop-in" }, { time: "5:00 PM", type: "Private 1-on-1" }, { time: "6:30 PM", type: "Private 1-on-1" }] },
  { day: "Thursday", slots: [{ time: "4:00 PM", type: "Group Drop-in" }, { time: "5:30 PM", type: "Private 1-on-1" }] },
  { day: "Friday", slots: [{ time: "3:30 PM", type: "Group Drop-in" }, { time: "5:00 PM", type: "Private 1-on-1" }] },
  { day: "Saturday", slots: [{ time: "9:00 AM", type: "Private 1-on-1" }, { time: "10:30 AM", type: "Group Drop-in" }, { time: "12:00 PM", type: "Group Drop-in" }] },
  { day: "Sunday", slots: [] },
];

const EMPTY_FORM = { name: "", email: "", phone: "", athlete_grade: "Middle School", session_type: "Private 1-on-1 ($60)", preferred_day: "Monday", preferred_slot: "", goals: "", notes: "", promo_code: "", film_file_id: "", film_filename: "", consent_photo: false, consent_audio: false, consent_video: false, consent_agreed: false, consent_signed_by: "" };

export default function Schedule() {
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [slotError, setSlotError] = useState("");
  const [film, setFilm] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleFilm = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads/film", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setFilm(data);
      setForm((f) => ({ ...f, film_file_id: data.id, film_filename: data.filename }));
      toast.success("Film attached — Theo will review it before your session.");
    } catch {
      toast.error("Film upload failed. Try a smaller file or different format.");
    } finally {
      setUploading(false);
    }
  };

  const removeFilm = () => {
    setFilm(null);
    setForm((f) => ({ ...f, film_file_id: "", film_filename: "" }));
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const pickSlot = (day, slot) => {
    setForm((f) => ({
      ...f,
      preferred_day: day,
      preferred_slot: slot.time,
      session_type: slot.type === "Private 1-on-1" ? "Private 1-on-1 ($60)" : "Group Drop-in ($30)",
    }));
    document.getElementById("booking-form")?.scrollIntoView({ behavior: "smooth" });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
      toast.error("Please fill in your name, email, and phone number.");
      return;
    }
    if (!form.preferred_slot) {
      setSlotError("Please pick a day and time slot from the weekly grid above — we can't hold a session without one.");
      toast.error("Pick a day and time slot above first.");
      return;
    }
    setSlotError("");
    const allConsented = form.consent_photo && form.consent_audio && form.consent_video;
    if (!allConsented || !form.consent_signed_by.trim()) {
      toast.error("Please complete the Media Consent & Release (all three permissions + guardian signature) before submitting.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/bookings", { ...form, consent_agreed: allConsented });
      setSent(true);
      toast.success("Booking request sent — Theo will confirm your slot shortly.");
      setForm(EMPTY_FORM);
      setFilm(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (err) {
      toast.error("Could not send the request. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="pt-16">
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
        <Reveal>
          <SectionTag index="SEC.01" label="Live Availability" />
          <h1 data-testid="schedule-heading" className="font-display font-black uppercase tracking-tighter leading-[0.9] text-4xl sm:text-5xl lg:text-6xl">
            Book your <span className="text-court-amber">session.</span>
          </h1>
          <p className="mt-5 text-base md:text-lg text-zinc-400 max-w-2xl leading-relaxed">
            Tap any open slot below to pre-fill your booking request. Theo confirms every request personally — usually within a few hours.
          </p>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-8 flex items-start gap-3 bg-court-amber/10 border border-court-amber/30 rounded-xl px-5 py-4 max-w-2xl">
            <DollarSign size={18} className="text-court-amber shrink-0 mt-0.5" />
            <p className="text-sm text-zinc-300"><span className="font-bold text-court-amber">No online payment needed.</span> Pay in person at the gym — cash, Zelle, or Venmo. $60 private / $30 group drop-in.</p>
          </div>
        </Reveal>

        {/* WEEKLY GRID */}
        <div data-testid="availability-grid" className="mt-12 grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3">
          {WEEKLY_SLOTS.map((d, di) => (
            <Reveal key={d.day} delay={di * 0.04}>
              <div className="bg-court-surface/80 border border-white/10 rounded-xl p-4 h-full">
                <div className="font-mono text-xs uppercase tracking-widest text-zinc-400 mb-3 flex items-center gap-2">
                  <Calendar size={12} className="text-court-amber" /> {d.day}
                </div>
                {d.slots.length === 0 ? (
                  <p className="text-xs text-zinc-600 font-mono uppercase tracking-wider">Rest day</p>
                ) : (
                  <div className="space-y-2">
                    {d.slots.map((s) => (
                      <button
                        key={s.time}
                        data-testid={`slot-${d.day.toLowerCase()}-${s.time.replace(/[:\s]/g, "-").toLowerCase()}`}
                        onClick={() => pickSlot(d.day, s)}
                        className={`w-full text-left rounded-lg px-3 py-2 border transition-colors duration-200 ${
                          form.preferred_day === d.day && form.preferred_slot === s.time
                            ? "border-court-amber bg-court-amber/15"
                            : "border-white/10 hover:border-court-amber/50 bg-court-void/50"
                        }`}
                      >
                        <div className="flex items-center gap-1.5 text-sm font-semibold text-zinc-100"><Clock size={12} className="text-court-amber" />{s.time}</div>
                        <div className={`font-mono text-[10px] uppercase tracking-widest mt-0.5 ${s.type === "Private 1-on-1" ? "text-court-amber" : "text-zinc-500"}`}>
                          {s.type === "Private 1-on-1" ? "1-on-1 • $60" : "Group • $30"}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </Reveal>
          ))}
        </div>

        {GOOGLE_CALENDAR_EMBED_URL && (
          <Reveal>
            <div className="mt-12">
              <SectionTag index="LIVE" label="Straight From Theo's Google Calendar" />
              <h2 className="font-display font-extrabold uppercase tracking-tight leading-[0.95] text-2xl sm:text-3xl lg:text-4xl">
                Real-time <span className="text-court-amber">availability.</span>
              </h2>
              <p className="mt-3 text-sm sm:text-base text-zinc-400 max-w-xl">This is Theo's actual calendar — if it's open here, it's open. Pick a time below, then send your request.</p>
              <div data-testid="google-calendar-embed" className="mt-6 bg-court-surface/80 border border-white/10 rounded-2xl p-3 sm:p-4 overflow-hidden">
                <iframe title="Theo's Google Calendar" src={GOOGLE_CALENDAR_EMBED_URL} className="w-full h-[600px] rounded-lg bg-white hidden sm:block" frameBorder="0" scrolling="no" />
                <iframe title="Theo's Google Calendar (agenda)" src={`${GOOGLE_CALENDAR_EMBED_URL}&mode=AGENDA&showTitle=0&showNav=0`} className="w-full h-[520px] rounded-lg bg-white sm:hidden" frameBorder="0" scrolling="no" />
              </div>
            </div>
          </Reveal>
        )}

        {/* BOOKING FORM */}
        <div id="booking-form" className="mt-16 grid lg:grid-cols-5 gap-10">
          <Reveal className="lg:col-span-2">
            <SectionTag index="SEC.02" label="Booking Request" />
            <h2 className="font-display font-extrabold uppercase tracking-tight leading-[0.95] text-2xl sm:text-3xl lg:text-4xl">Lock in your <span className="text-court-amber">spot.</span></h2>
            <ul className="mt-6 space-y-4">
              {["Pick an open slot from the weekly grid", "Send the request — Theo confirms personally", "Show up and pay in person (cash / Zelle / Venmo)"].map((s, i) => (
                <li key={s} className="flex gap-4">
                  <span className="font-display font-black text-2xl text-outline shrink-0">0{i + 1}</span>
                  <span className="text-sm text-zinc-300 pt-1">{s}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8 flex items-start gap-3 bg-court-surface/80 border border-white/10 rounded-xl px-5 py-4">
              <Info size={16} className="text-court-amber shrink-0 mt-0.5" />
              <div className="space-y-2">
                <p className="text-xs text-zinc-400 leading-relaxed">The weekly grid above mirrors Theo's live Google Calendar (embedded below). Booking requests are confirmed personally by Theo.</p>
                <p className="text-xs text-zinc-400 leading-relaxed">All sessions at <span className="text-court-amber font-semibold">Nothing But Net Gym</span> — 590 Whitney St, San Leandro, CA 94577.</p>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1} className="lg:col-span-3">
            {sent && (
              <div data-testid="booking-success" className="mb-6 flex items-center gap-3 bg-emerald-500/10 border border-emerald-500/40 rounded-xl px-5 py-4">
                <CheckCircle2 size={18} className="text-emerald-400" />
                <p className="text-sm text-emerald-300">Request received. Theo will reach out to confirm.</p>
              </div>
            )}
            <form onSubmit={submit} noValidate className="bg-court-surface/90 border border-white/10 rounded-2xl p-7 space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <input data-testid="booking-name-input" className="input-dark" placeholder="Parent / athlete full name" value={form.name} onChange={set("name")} required />
                <input data-testid="booking-email-input" type="email" className="input-dark" placeholder="Email address" value={form.email} onChange={set("email")} required />
                <input data-testid="booking-phone-input" className="input-dark" placeholder="Phone number" value={form.phone} onChange={set("phone")} required />
                <select data-testid="booking-grade-select" className="input-dark" value={form.athlete_grade} onChange={set("athlete_grade")}>
                  {["Middle School", "JV", "Varsity", "College / D1"].map((g) => <option key={g}>{g}</option>)}
                </select>
                <select data-testid="booking-type-select" className="input-dark" value={form.session_type} onChange={set("session_type")}>
                  {["Private 1-on-1 ($60)", "Group Drop-in ($30)", "College Recruit Package ($399/mo)", "Starting 5 Varsity Package ($299/mo)", "JV Jumpstart Package ($149/mo)", "Rookie Foundations Package ($99/mo)", "Drop-in 10-Pack ($250)", "Summer Camp / Mini-Camp Inquiry"].map((t) => <option key={t}>{t}</option>)}
                </select>
                <div className="grid grid-cols-2 gap-4">
                  <select data-testid="booking-day-select" className="input-dark" value={form.preferred_day}
                    onChange={(e) => { setForm((f) => ({ ...f, preferred_day: e.target.value, preferred_slot: "" })); setSlotError(""); }}>
                    {WEEKLY_SLOTS.map((d) => <option key={d.day}>{d.day}</option>)}
                  </select>
                  <select data-testid="booking-slot-select" className={`input-dark ${slotError ? "border-red-500" : ""}`} value={form.preferred_slot}
                    onChange={(e) => {
                      const s = (WEEKLY_SLOTS.find((d) => d.day === form.preferred_day)?.slots || []).find((x) => x.time === e.target.value);
                      setForm((f) => ({ ...f, preferred_slot: e.target.value, ...(s ? { session_type: s.type === "Private 1-on-1" ? "Private 1-on-1 ($60)" : "Group Drop-in ($30)" } : {}) }));
                      setSlotError("");
                    }} required>
                    <option value="">Select a time slot</option>
                    {(WEEKLY_SLOTS.find((d) => d.day === form.preferred_day)?.slots || []).map((s) => (
                      <option key={s.time} value={s.time}>{s.time} — {s.type === "Private 1-on-1" ? "1-on-1 ($60)" : "Group ($30)"}</option>
                    ))}
                    {(WEEKLY_SLOTS.find((d) => d.day === form.preferred_day)?.slots || []).length === 0 && (
                      <option value="" disabled>No sessions — pick another day</option>
                    )}
                  </select>
                </div>
                {slotError && <p data-testid="slot-error" className="text-sm text-red-400">{slotError}</p>}
              </div>
              <input data-testid="booking-goals-input" className="input-dark" placeholder="Key skill goals (shooting, handles, agility, basketball IQ...)" value={form.goals} onChange={set("goals")} />
              <input data-testid="booking-promo-input" className="input-dark" placeholder="Promo code (optional — e.g. FIRST10)" value={form.promo_code} onChange={set("promo_code")} />
              <textarea data-testid="booking-notes-input" className="input-dark min-h-[90px]" placeholder="Notes for Coach Theo (injuries, schedule constraints, anything else)" value={form.notes} onChange={set("notes")} />

              <div className="border border-dashed border-white/20 rounded-xl p-5 bg-court-void/40">
                <div className="flex items-center gap-2 mb-2">
                  <FileVideo size={16} className="text-court-amber" />
                  <span className="font-mono text-xs uppercase tracking-widest text-zinc-300">Game film (optional)</span>
                </div>
                <p className="text-xs text-zinc-500 mb-3">Send film ahead of time and Theo breaks it down before your first session — you walk in with a plan.</p>
                {film ? (
                  <div data-testid="film-attached" className="flex items-center justify-between gap-3 bg-court-surface border border-emerald-500/40 rounded-lg px-4 py-3">
                    <span className="text-sm text-zinc-200 truncate">{film.filename} <span className="text-zinc-500">({(film.size / 1024 / 1024).toFixed(1)} MB)</span></span>
                    <button type="button" data-testid="film-remove-btn" onClick={removeFilm} className="text-zinc-500 hover:text-red-400 transition-colors shrink-0"><X size={16} /></button>
                  </div>
                ) : (
                  <button type="button" data-testid="film-upload-btn" disabled={uploading}
                    onClick={() => fileInputRef.current?.click()}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => { e.preventDefault(); handleFilm(e.dataTransfer.files?.[0]); }}
                    className="w-full flex items-center justify-center gap-2 border border-white/15 hover:border-court-amber/50 rounded-lg px-4 py-4 text-sm text-zinc-400 hover:text-court-amber transition-colors disabled:opacity-60">
                    <UploadCloud size={16} /> {uploading ? "Uploading film..." : "Drop video here or click to browse (MP4, MOV — max 500MB)"}
                  </button>
                )}
                <input ref={fileInputRef} type="file" accept="video/*" className="hidden" data-testid="film-file-input" onChange={(e) => handleFilm(e.target.files?.[0])} />
              </div>

              <div data-testid="consent-block" className="border border-court-amber/30 bg-court-amber/5 rounded-xl p-5 space-y-4">
                  <div className="font-mono text-xs uppercase tracking-widest text-court-amber font-bold">Media Consent &amp; Release — required for every athlete</div>
                  <p className="text-xs text-zinc-400 leading-relaxed">
                    I hereby authorize <span className="text-zinc-200 font-semibold">Theo Basketball Lab</span> (the "Organization"), and its partners, agents, representatives, assigns, successors in interest and licensees, to photograph, audiotape, and/or videotape the athlete named above, and grant the Organization the right to use such photographs, audio recordings, and video recordings for coaching review, player development, and recruiting preparation. I understand that I will not receive any monetary compensation for these permissions. I release and discharge the Organization from any and all claims arising out of or in connection with these recordings, including claims for libel, invasion of privacy, or emotional distress. I understand that I cannot withdraw my consent after signing this form, and that this consent and release is binding on me and my heirs, legal representatives, and assigns.
                  </p>
                  <div className="space-y-2.5">
                    {[
                      ["consent_photo", "consent-photo-checkbox", "Yes — I grant permission for Photographs to be collected and used by the Organization."],
                      ["consent_audio", "consent-audio-checkbox", "Yes — I grant permission for Audio to be collected and used by the Organization."],
                      ["consent_video", "consent-video-checkbox", "Yes — I grant permission for Video to be collected and used by the Organization."],
                    ].map(([key, testid, label]) => (
                      <label key={key} className="flex items-start gap-3 cursor-pointer">
                        <input type="checkbox" data-testid={testid} checked={form[key]}
                          onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.checked }))}
                          className="mt-0.5 w-4 h-4 accent-[#FF6B00]" />
                        <span className="text-sm text-zinc-300">{label}</span>
                      </label>
                    ))}
                  </div>
                  <p className="text-xs text-zinc-400 leading-relaxed">
                    I am the parent or legal guardian of the athlete named above, and I hereby sign this Media Consent and Release on behalf of such individual in accordance with the statements above.
                  </p>
                  <input data-testid="consent-signature-input" className="input-dark" placeholder="Parent / guardian full name (this is your online signature)"
                    value={form.consent_signed_by} onChange={set("consent_signed_by")} />
                  <p className="text-[11px] font-mono text-zinc-600 uppercase tracking-wider">Signed electronically on submission — date recorded automatically • no in-person signature needed</p>
                </div>

              <button data-testid="booking-submit-btn" type="submit" disabled={busy}
                className="btn-amber w-full bg-court-amber text-court-void font-display font-extrabold uppercase tracking-wide text-lg py-3.5 rounded-lg disabled:opacity-60">
                {busy ? "Sending..." : "Send Booking Request"}
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
